"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VumService = exports.VumUserRoleStatus = void 0;
const types_1 = require("../shared/types");
const PermissionService_1 = require("../permissionService/PermissionService");
const CommissionIdConverter_1 = require("./CommissionIdConverter");
const NotEnrolledVehicleError_1 = require("./errors/NotEnrolledVehicleError");
const VumFlattenedConverter_1 = require("./VumFlattenedConverter");
var VumUserRoleStatus;
(function (VumUserRoleStatus) {
    VumUserRoleStatus["DISABLED_HMI"] = "DISABLED_HMI";
    VumUserRoleStatus["DISABLED_SPIN"] = "DISABLED_SPIN";
    VumUserRoleStatus["DISABLED_PU_SPIN_RESET"] = "DISABLED_PU_SPIN_RESET";
    VumUserRoleStatus["ENABLED"] = "ENABLED";
    VumUserRoleStatus["UNKNOWN"] = "UNKNOWN";
})(VumUserRoleStatus || (exports.VumUserRoleStatus = VumUserRoleStatus = {}));
class VumService {
    constructor(vumClient, vdbsService, permissionService) {
        this.vumClient = vumClient;
        this.vdbsService = vdbsService;
        this.permissionService = permissionService;
    }
    async getVehicles() {
        const vumResponse = await this.vumClient.getUserVehicleRelations();
        let vehicles = [];
        if (vumResponse.relations) {
            const enrichedVehicles = await this.loadDevicePlatformIfMissing(vumResponse);
            vehicles = VumFlattenedConverter_1.VumFlattenedConverter.convertUserVehicleRelationsToFlattenedVehicles(enrichedVehicles);
        }
        return vehicles;
    }
    async getVehicle(vin) {
        var _a;
        const vumResponse = await this.vumClient.getUserVehicleRelation(vin);
        let relation = vumResponse.relation;
        if (!relation) {
            return undefined;
        }
        if (!((_a = relation.vehicle) === null || _a === void 0 ? void 0 : _a.modBackend)) {
            const modBackend = await this.vdbsService.getDevicePlatform(vin);
            relation = Object.assign(Object.assign({}, relation), { vehicle: Object.assign(Object.assign({}, relation.vehicle), { modBackend }) });
        }
        return VumFlattenedConverter_1.VumFlattenedConverter.convertUserVehicleRelationsToFlattenedVehicles([relation])[0];
    }
    async getVehicleRelations(vin) {
        const vumResponse = await this.vumClient.getVehicleRelations(vin);
        let relations = vumResponse.relations;
        if (!relations) {
            return undefined;
        }
        return VumFlattenedConverter_1.VumFlattenedConverter.convertVehicleRelationsToFlattenedUsers(relations);
    }
    async getUserRoleStatus(vin) {
        var _a;
        const vumResponse = await this.vumClient.getUserVehicleRelation(vin);
        let relation = vumResponse === null || vumResponse === void 0 ? void 0 : vumResponse.relation;
        if (!relation) {
            throw Error('User role status undefined');
        }
        switch ((_a = relation.roleStatus) === null || _a === void 0 ? void 0 : _a.toLowerCase()) {
            case 'enabled':
                return VumUserRoleStatus.ENABLED;
            case 'disabled_hmi':
                return VumUserRoleStatus.DISABLED_HMI;
            case 'disabled_spin':
                return VumUserRoleStatus.DISABLED_SPIN;
            case 'disabled_pu_spin_reset':
                return VumUserRoleStatus.DISABLED_PU_SPIN_RESET;
            default:
                return VumUserRoleStatus.UNKNOWN;
        }
    }
    async updateNicknameAndLicensePlateForVehicle(vehicle) {
        var _a, _b;
        const dataToUpdate = [
            {
                op: 'replace',
                path: '/vehicleNickname',
                value: (_a = vehicle.nickname) !== null && _a !== void 0 ? _a : '',
            },
            {
                op: 'replace',
                path: '/licensePlate',
                value: (_b = vehicle.licensePlateNumber) !== null && _b !== void 0 ? _b : '',
            },
        ];
        if (vehicle.vin) {
            await this.vumClient.patchVinVehicle(vehicle.vin, dataToUpdate);
        }
        else {
            const commissionData = {
                commissionNumber: vehicle.commissionNumber,
                bid: vehicle.bid,
                commissionNumberYear: vehicle.commissionNumberYear,
            };
            await this.vumClient.patchCommissionVehicle(commissionData, dataToUpdate);
        }
    }
    async updatePrimaryVehicle(vehicle, isPrimaryCar) {
        const dataToUpdate = [
            {
                op: 'replace',
                path: '/primaryCar',
                value: String(isPrimaryCar),
            },
        ];
        if (vehicle.vin) {
            await this.vumClient.patchVinVehicle(vehicle.vin, dataToUpdate);
        }
        else {
            const commissionData = {
                commissionNumber: vehicle.commissionNumber,
                bid: vehicle.bid,
                commissionNumberYear: vehicle.commissionNumberYear,
            };
            await this.vumClient.patchCommissionVehicle(commissionData, dataToUpdate);
        }
    }
    async addVinToCommissionDataVehicle(vehicle, vin) {
        const commissionId = CommissionIdConverter_1.CommissionIdConverter.convertCommissionDataToId(vehicle.commissionNumber, vehicle.bid, vehicle.commissionNumberYear);
        await this.vumClient.addVinToUserVehicle(commissionId, vin);
    }
    async addVehicle(vehicle) {
        if (this.hasVehicleIdentifier(vehicle)) {
            await this.vumClient.addUserVehicleRelation(VumFlattenedConverter_1.VumFlattenedConverter.convertFlattenedVehicleToUserVehicleRelation(vehicle));
            return this.getAddedVehicleOrFallback(vehicle);
        }
        else {
            throw Error('Invalid vehicle');
        }
    }
    async deleteVehicle(vehicle) {
        if (vehicle.vin || (vehicle.commissionNumber && vehicle.bid && vehicle.commissionNumberYear)) {
            await this.vumClient.deleteUserVehicleRelation(VumFlattenedConverter_1.VumFlattenedConverter.convertFlattenedVehicleToUserVehicleRelation(vehicle));
        }
        else {
            throw Error('Invalid vehicle');
        }
    }
    async disenrollUser(vehicle) {
        if (!vehicle.vin) {
            throw new NotEnrolledVehicleError_1.NotEnrolledVehicleError();
        }
        const { PU_COMPLETED, GU, SU } = PermissionService_1.PermissionGroup;
        if (this.permissionService.hasPermission(vehicle, PU_COMPLETED)) {
            await this.vumClient.resetPrimaryUser(vehicle.vin);
        }
        else if (this.permissionService.hasPermission(vehicle, SU, GU)) {
            await this.vumClient.resetNonPrimaryUser(vehicle.vin);
        }
        else {
            throw new NotEnrolledVehicleError_1.NotEnrolledVehicleError();
        }
    }
    async getMbbUserId() {
        var _a, _b;
        const vumResponse = await this.vumClient.getUserVehicleRelations();
        return (_b = (_a = vumResponse === null || vumResponse === void 0 ? void 0 : vumResponse.user) === null || _a === void 0 ? void 0 : _a.mbbUserId) !== null && _b !== void 0 ? _b : '';
    }
    async deleteUser() {
        await this.vumClient.deleteUser();
    }
    async removeUserFromVehicle(userId, vin) {
        await this.vumClient.removeUserFromVehicle(userId, vin);
    }
    async loadDevicePlatformIfMissing(vumResponse) {
        return Promise.all(vumResponse.relations.map(async (relation) => {
            var _a, _b, _c;
            const modBackend = (_b = (_a = relation.vehicle) === null || _a === void 0 ? void 0 : _a.modBackend) !== null && _b !== void 0 ? _b : (await this.vdbsService.getDevicePlatform((_c = relation.vehicle) === null || _c === void 0 ? void 0 : _c.vin));
            return Object.assign(Object.assign({}, relation), { vehicle: Object.assign(Object.assign({}, relation.vehicle), { modBackend }) });
        }));
    }
    hasVehicleIdentifier(vehicle) {
        return vehicle.vin || (vehicle.commissionNumber && vehicle.bid && vehicle.commissionNumberYear);
    }
    async getAddedVehicleOrFallback(vehicle) {
        var _a, _b;
        const fallbackVehicle = Object.assign(Object.assign({}, vehicle), { role: types_1.Role.UNKNOWN, enrollmentStatus: types_1.EnrollmentStatus.UNKNOWN, devicePlatform: types_1.DevicePlatform.UNKNOWN, primaryCar: false });
        try {
            if (vehicle.vin) {
                return (_a = (await this.getVehicle(vehicle.vin))) !== null && _a !== void 0 ? _a : fallbackVehicle;
            }
            else {
                return (_b = (await this.findMatchingCommissionDataVehicle(vehicle))) !== null && _b !== void 0 ? _b : fallbackVehicle;
            }
        }
        catch (e) {
            return fallbackVehicle;
        }
    }
    async findMatchingCommissionDataVehicle(addedVehicle) {
        const allVehicles = await this.getVehicles();
        return allVehicles.find((vehicle) => vehicle.commissionNumber === addedVehicle.commissionNumber &&
            vehicle.commissionNumberYear === addedVehicle.commissionNumberYear &&
            vehicle.bid === addedVehicle.bid);
    }
    async patchVinVehicle(vin, payload) {
        return await this.vumClient.patchVinVehicle(vin, payload);
    }
}
exports.VumService = VumService;
//# sourceMappingURL=VumService.js.map